import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { isNativeVideo, toEmbed } from "./video-url";

// Remember the user's sound preference; each new video may still need a tap.
let soundUnlocked = false;

export function VideoScreen({ url, title, forceMuted = false }: {
  url: string; title: string; forceMuted?: boolean;
}) {
  const iframe = useRef<HTMLIFrameElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(forceMuted || !soundUnlocked);
  // Always offer a tap until native playback actually starts. On iPad, play()
  // can remain pending or a visibility pause can occur without rejecting it.
  const [blocked, setBlocked] = useState(() => isNativeVideo(url));
  const [failed, setFailed] = useState(false);
  const native = isNativeVideo(url);
  // Unmuting via the iframe API must not reload the video from its beginning.
  const [embed] = useState(() => toEmbed(url, forceMuted || !soundUnlocked));
  const silent = forceMuted || muted;

  const command = (func: string, args = "") =>
    iframe.current?.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func, args }),
      new URL(embed).origin,
    );

  useEffect(() => {
    if (native) {
      let active = true;
      const element = video.current;
      if (!element) return;
      element.defaultMuted = silent;
      element.muted = silent;
      element.playsInline = true;
      element.play().catch(() => {
        if (active) setBlocked(true);
      });
      return () => { active = false; };
    }
    const timers = [1500, 3500].map((ms) =>
      window.setTimeout(() => command("playVideo"), ms),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [url, native]);

  const play = () => {
    // Keep the tap target visible until 'playing', including slow iPad loads.
    if (!native) setBlocked(false);
    setFailed(false);
    if (!forceMuted) {
      soundUnlocked = true;
      setMuted(false);
    }
    if (native && video.current) {
      video.current.defaultMuted = forceMuted;
      video.current.muted = forceMuted;
      if (video.current.error) video.current.load();
      video.current.play().catch(() => setBlocked(true));
    } else {
      if (!forceMuted) { command("unMute"); command("setVolume", "100"); }
      command("playVideo");
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="relative h-full w-full bg-ink">
      {native ? (
        <video ref={video} src={url} aria-label={title || "فيديو"}
          className="h-full w-full object-contain" autoPlay playsInline loop muted={silent}
          preload="auto" onPlaying={() => { setBlocked(false); setFailed(false); }}
          onPause={() => setBlocked(true)}
          onError={() => setFailed(true)} />
      ) : (
        <iframe ref={iframe} title={title || "فيديو"} src={embed}
          className="h-full w-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
      )}
      {failed && (
        <div role="alert" className="absolute inset-0 flex items-center justify-center bg-ink/90 p-12 text-center text-2xl text-white" dir="rtl">
          تعذّر تحميل الفيديو. تحقّق من اتصال الإنترنت ثم أعد المحاولة.
        </div>
      )}
      {(failed || blocked || (muted && !forceMuted)) && (
        <motion.button initial={{ opacity: 1 }} animate={{ opacity: 1 }}
          onClick={play}
          className="absolute bottom-8 left-1/2 z-10 min-h-14 -translate-x-1/2 touch-manipulation cursor-pointer
                     rounded-full border border-line bg-ink-2/80 px-6 py-3
                     text-lg font-bold text-fg shadow-2xl backdrop-blur-md hover:border-c1">
          <span className="gradient-text">{failed ? "إعادة المحاولة" : forceMuted ? "اضغط لتشغيل الفيديو" : "اضغط للتشغيل بالصوت"}</span>
        </motion.button>
      )}
    </motion.div>
  );
}
